import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { workOrderService } from '../../../services/workOrderService';
import { inventoryService } from '../../../services/inventoryService';
import { machineService } from '../../../services/machineService';
import { machineTypeService } from '../../../services/machineTypeService';
import type { Machine } from '../../../types/machine';
import type { MachineType } from '../../../types/machineType';
import { groupItemsByDate } from '../../../utils/date';
import { useSort } from '../../../hooks/useSort';
import { useToast } from '../../../components/common/Toast';
import type { WorkOrder, CreateWorkOrderData, WorkOrderStatus } from '../../../types/workOrder';
import type { InventoryItem, InventoryItemFormData } from '../../../types/inventory';
import { useLanguage } from '../../../features/language/useLanguage';
import { usePaginationState } from '../../../hooks/usePaginationState';
import { SUBSCRIPTION_QUERY_KEY } from '../../../hooks/useSubscription';
import type { RoastType } from '../../../constants/roastTypes';

const resolveBatchCogs = (cogs?: {
  total_input_cost?: number;
  unit_cost?: number;
  output_quantity?: number;
  standard_cost?: number;
}) => {
  if (!cogs) {
    return 0;
  }

  if (typeof cogs.total_input_cost === 'number' && Number.isFinite(cogs.total_input_cost) && cogs.total_input_cost > 0) {
    return cogs.total_input_cost;
  }

  if (typeof cogs.standard_cost === 'number' && Number.isFinite(cogs.standard_cost) && cogs.standard_cost > 0) {
    return cogs.standard_cost;
  }

  if (
    typeof cogs.unit_cost === 'number' &&
    Number.isFinite(cogs.unit_cost) &&
    cogs.unit_cost > 0 &&
    typeof cogs.output_quantity === 'number' &&
    Number.isFinite(cogs.output_quantity) &&
    cogs.output_quantity > 0
  ) {
    return cogs.unit_cost * cogs.output_quantity;
  }

  if (typeof cogs.unit_cost === 'number' && Number.isFinite(cogs.unit_cost) && cogs.unit_cost > 0) {
    return cogs.unit_cost;
  }

  return 0;
};

export type DerivedWorkOrder = WorkOrder & {
  primaryRecipeName: string;
  shrinkageKg: number | null;
  shrinkagePercent: number | null;
  resolvedItemCode?: string;
  resolvedItemName?: string;
};

// PRD 76: error payload returned by the backend when a roast write would
// exceed the per-cycle quota. Mirrored from CreateWorkOrderPage.
export interface QuotaExceededPayload {
  error: 'subscription.quota_exceeded';
  tier: string;
  limit_kg: number;
  used_kg: number;
  requested_kg: number;
  cycle_end: string;
}

const isQuotaExceededError = (err: unknown): err is { response: { data: QuotaExceededPayload } } => {
  const payload = (err as { response?: { data?: { error?: unknown } } })?.response?.data;
  return !!payload && payload.error === 'subscription.quota_exceeded';
};

export const useWorkOrdersPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [isFinishedGoodsModalOpen, setIsFinishedGoodsModalOpen] = React.useState(false);
  const [editingOrder, setEditingOrder] = React.useState<WorkOrder | null>(null);
  const [quotaError, setQuotaError] = React.useState<QuotaExceededPayload | null>(null);
  const clearQuotaError = React.useCallback(() => setQuotaError(null), []);
  const [selectedWorkOrder, setSelectedWorkOrder] = React.useState<WorkOrder | null>(null);
  const [searchFilter, setSearchFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<string[]>([]);
  const [roastTypeFilter, setRoastTypeFilter] = React.useState<string[]>([]);
  const [machineFilter, setMachineFilter] = React.useState<string[]>([]);
  const [machineTypeFilter, setMachineTypeFilter] = React.useState<string[]>([]);
  const [finishedGoodInitialData, setFinishedGoodInitialData] = React.useState<Partial<InventoryItemFormData> | null>(null);
  const [finishedGoodMode, setFinishedGoodMode] = React.useState<'create' | 'update'>('create');
  const [selectedInventoryItemId, setSelectedInventoryItemId] = React.useState<string | null>(null);
  // Work order awaiting completion, held until the Move to Inventory modal is submitted.
  // Completing is not a flag flip — it receives stock, computes COGS and creates the
  // roasted_bulk item — so none of it may run until the user confirms.
  const [pendingCompletionId, setPendingCompletionId] = React.useState<string | null>(null);

  const { data: orders = [], isLoading: loading } = useQuery({
    queryKey: ['work-orders'],
    queryFn: workOrderService.getAll,
  });

  const { data: inventoryItems = [] } = useQuery<InventoryItem[]>({
    queryKey: ['inventory-items-all'],
    queryFn: () => inventoryService.getAll(),
  });

  const { data: machines = [] } = useQuery<Machine[]>({
    queryKey: ['machines-all'],
    queryFn: () => machineService.getAll(),
  });

  const { data: machineTypes = [] } = useQuery<MachineType[]>({
    queryKey: ['machine-types-all'],
    queryFn: () => machineTypeService.getAll(),
  });

  const machineTypeToMachineId = React.useMemo(() => {
    const map = new Map<string, string>();
    machineTypes.forEach(mt => map.set(mt.id, mt.machineId));
    return map;
  }, [machineTypes]);

  React.useEffect(() => {
    if (machineFilter.length === 0) return;
    setMachineTypeFilter(current => {
      if (current.length === 0) return current;
      const allowedMachines = new Set(machineFilter);
      const next = current.filter(typeId => {
        const machineId = machineTypeToMachineId.get(typeId);
        return machineId ? allowedMachines.has(machineId) : false;
      });
      return next.length === current.length ? current : next;
    });
  }, [machineFilter, machineTypeToMachineId]);

  const handleCreateWorkOrder = () => {
    navigate('/work-orders/create');
  };

  const formatPercent = (value: number | null | undefined) => {
    if (value === null || value === undefined) return null;
    if (!Number.isFinite(value)) return null;
    if (Math.abs(value - 100) < 0.000001) {
      return '100';
    }
    return value.toFixed(3);
  };

  const handleEditOrder = React.useCallback(
    async (orderId: string) => {
      try {
        const order = await workOrderService.getById(orderId);
        if (order) {
          setEditingOrder(order);
        }
      } catch (err) {
        console.error('Failed to fetch work order details:', err);
        showToast(t('workOrders.page.errors.loadDetailsFailed'), 'error');
      }
    },
    [showToast, t]
  );

  React.useEffect(() => {
    const openId = searchParams.get('openId');
    if (openId) {
      setTimeout(() => {
        handleEditOrder(openId);
      }, 0);
    }
  }, [searchParams, handleEditOrder]);

  const handleUpdateOrder = async (data: CreateWorkOrderData, action: 'draft' | 'schedule' | 'complete') => {
    if (!editingOrder) return;
    try {
      const updated = await workOrderService.update(editingOrder.id, data);
      if (editingOrder.status === 'draft' && (action === 'schedule' || action === 'complete')) {
        try {
          await workOrderService.updateStatus(editingOrder.id, 'scheduled');
          if (action === 'complete') {
            const completed = await workOrderService.updateStatus(editingOrder.id, 'completed', data.outputExpiryDate);
            const orderForInventory = { ...completed, outputWeight: data.outputWeight ?? updated.outputWeight };
            await inventoryService.createFromWorkOrder(editingOrder.id, orderForInventory, data.outputExpiryDate);
          }
        } catch (statusErr) {
          console.error('Failed to move work order to scheduled/completed:', statusErr);
          if (isQuotaExceededError(statusErr)) {
            setQuotaError(statusErr.response.data);
            queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
            return;
          }
          const err = statusErr as { response?: { data?: { message?: string; error?: string } } };
          const message =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            t('workOrders.page.errors.selectAtLeastOneLot');
          showToast(message, 'error');
          return;
        }
      } else if (action === 'complete') {
        const completedOrder = await workOrderService.updateStatus(editingOrder.id, 'completed', data.outputExpiryDate);
        const orderForInventory = { ...completedOrder, outputWeight: data.outputWeight ?? updated.outputWeight };
        await inventoryService.createFromWorkOrder(editingOrder.id, orderForInventory, data.outputExpiryDate);
      }

      setEditingOrder(null);
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
      if (action === 'complete') {
        queryClient.invalidateQueries({ queryKey: ['inventory-items-all'] });
      }
    } catch (err) {
      console.error('Failed to update work order:', err);
      if (isQuotaExceededError(err)) {
        setQuotaError(err.response.data);
        queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
        return;
      }
      showToast(t('workOrders.page.errors.updateFailed'), 'error');
    }
  };

  // Opens the Move to Inventory modal as a confirmation step for completing `order`.
  // Nothing is sent to the server here; handleCreateFinishedGood commits on submit.
  const requestCompletion = async (order: WorkOrder) => {
    setPendingCompletionId(order.id);
    await handleMoveToInventory(order);
  };

  const handleStatusChange = async (id: string, newStatus: WorkOrderStatus) => {
    if (newStatus === 'completed') {
      const order = orders.find(o => o.id === id);
      if (order) {
        await requestCompletion(order);
      }
      return;
    }

    try {
      await workOrderService.updateStatus(id, newStatus);
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
    } catch (err) {
      console.error('Failed to update status:', err);
      if (isQuotaExceededError(err)) {
        setQuotaError(err.response.data);
        queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
      }
    }
  };

  const handleCompleteFromCard = async (id: string) => {
    const order = orders.find(o => o.id === id);
    if (!order) return;
    await requestCompletion(order);
  };

  const handleCancelOrder = async (id: string) => {
    await workOrderService.updateStatus(id, 'cancelled');
    queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ['inventory-items-all'] });
    // Cancelling releases any linked Artisan profile (is_synced -> false) server-side.
    // The Artisan Sync list caches with a 5-min staleTime, so without invalidating it here
    // it would keep showing the profile as Synced until that window elapsed.
    queryClient.invalidateQueries({ queryKey: ['artisan-profiles'] });
  };

  const handleDeleteOrder = async (order: WorkOrder) => {
    if (order.status !== 'cancelled') {
      showToast(t('workOrders.page.errors.onlyCancelledDelete'), 'error');
      return;
    }

    await workOrderService.delete(order.id);
    queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
  };

  const handleMoveToInventory = async (order: WorkOrder) => {
    setSelectedWorkOrder(order);
    const existingItemForWO = inventoryItems.find(item => item.workOrderId === order.id);
    if (existingItemForWO) {
      setFinishedGoodMode('update');
      setSelectedInventoryItemId(existingItemForWO.id);
      setFinishedGoodInitialData({
        code: existingItemForWO.code,
        name: existingItemForWO.name,
        description: existingItemForWO.description,
        unitOfMeasure: existingItemForWO.unitOfMeasure,
        itemType: existingItemForWO.itemType,
        standardCost: existingItemForWO.standardCost,
        quantity: order.outputWeight ?? 0,
        expiryDate: existingItemForWO.expiryDate,
        workOrderId: order.id,
        location: existingItemForWO.location,
        green_bean_profile: existingItemForWO.green_bean_profile,
        greenBeanProfileId: existingItemForWO.greenBeanProfileId,
        newGreenBeanProfile: existingItemForWO.newGreenBeanProfile,
        roastInputWeight: order.inputWeight ?? 0,
      });
      setIsFinishedGoodsModalOpen(true);
      return;
    }

    let standardCost = 0;
    let roastTotalInputCost = 0;
    try {
      const cogs = await workOrderService.getRoastCogs(order.id);
      const batchCost = resolveBatchCogs(cogs);
      if (batchCost > 0) {
        roastTotalInputCost = batchCost;
        standardCost = batchCost;
      }
    } catch (err) {
      console.error('Failed to calculate roast COGS for work order:', err);
    }

    const quantity = order.outputWeight ?? 0;
    const resolvedName =
      order.recipeName && order.recipeName !== 'Unknown Recipe'
        ? order.recipeName
        : `Roasting #${order.workOrderNumber ?? order.id}`;
    setFinishedGoodMode('create');
    setSelectedInventoryItemId(null);
    setFinishedGoodInitialData({
      name: resolvedName,
      code: undefined,
      itemType: 'roasted_bulk',
      workOrderId: order.id,
      standardCost,
      unitOfMeasure: 'kg',
      quantity,
      description: `Produced from Work Order #${order.id}`,
      roastInputWeight: order.inputWeight ?? 0,
      roastTotalInputCost: roastTotalInputCost || standardCost,
    });
    setIsFinishedGoodsModalOpen(true);
  };

  const handleCreateFinishedGood = async (data: InventoryItemFormData) => {
    try {
      if (pendingCompletionId) {
        // This submit is the confirmation for completing the work order. One call does the
        // whole thing server-side: status, COGS, the roasted_bulk item and its stock receipt.
        const completed = await workOrderService.complete(pendingCompletionId, data.expiryDate);
        setPendingCompletionId(null);

        // The roast output is the inventory item that REFERENCES this work order
        // (inventory_items.wo_reference_id), never `completed.itemId`:
        // work_orders.item_id is the INPUT green bean the roast consumed — the backend
        // calls it bean_id everywhere else, and the All-list column shows its name.
        // Writing this payload onto it renamed the user's green bean to
        // "Roasting #<work order>" and flipped item_type to roasted_bulk, so the bean
        // dropped out of every green-bean picker and out of the Inventory green-bean
        // tab. Only the draft-then-Complete path went through here; completing at
        // create time goes through createFromWorkOrder, which already resolves the
        // output item by work order id, which is why that path kept the right name.
        //
        // handleMoveToInventory has already resolved the linked output item when one
        // existed. Otherwise complete() may have just created it (it does so when the
        // work order had no item linked at all), so look it up before falling back to
        // creating one — creating blindly would leave two roasted_bulk items against
        // the same work order.
        const payload: InventoryItemFormData = { ...data, workOrderId: completed.id };
        let outputItemId = selectedInventoryItemId;
        if (!outputItemId) {
          try {
            const items = await inventoryService.getAll();
            outputItemId = items.find(i => i.workOrderId === completed.id)?.id ?? null;
          } catch (lookupErr) {
            console.warn('Failed to look up the roast output item, creating a new one:', lookupErr);
          }
        }
        if (outputItemId) {
          await inventoryService.update(outputItemId, payload);
        } else {
          await inventoryService.create(payload);
        }
        showToast(t('workOrders.page.success.roastedBulkCreated'), 'success');
      } else {
        const workOrderId = selectedWorkOrder?.id || data.workOrderId;
        const payload: InventoryItemFormData = {
          ...data,
          workOrderId,
        };

        if (finishedGoodMode === 'update' && selectedInventoryItemId) {
          await inventoryService.update(selectedInventoryItemId, payload);
          showToast(t('workOrders.page.success.roastedBulkUpdated'), 'success');
        } else {
          await inventoryService.create(payload);
          showToast(t('workOrders.page.success.roastedBulkCreated'), 'success');
        }
      }

      setIsFinishedGoodsModalOpen(false);
      setSelectedWorkOrder(null);
      setSelectedInventoryItemId(null);
      setFinishedGoodInitialData(null);
      queryClient.invalidateQueries({ queryKey: ['inventory-items-all'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
    } catch (err) {
      // The roast quota is enforced at completion, which now happens on this submit.
      if (isQuotaExceededError(err)) {
        setPendingCompletionId(null);
        setIsFinishedGoodsModalOpen(false);
        setQuotaError(err.response.data);
        queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
        return;
      }
      console.error('Failed to save finished good:', err);
      showToast(t('workOrders.page.errors.saveFinishedGoodFailed'), 'error');
    }
  };

  // Dismissing the modal must drop the pending completion, otherwise the next unrelated
  // Move to Inventory submit would silently complete the work order left behind here.
  const closeFinishedGoodsModal = () => {
    setIsFinishedGoodsModalOpen(false);
    setPendingCompletionId(null);
    setSelectedWorkOrder(null);
    setSelectedInventoryItemId(null);
    setFinishedGoodInitialData(null);
  };

  const statusOptions = ['draft', 'scheduled', 'completed', 'cancelled'];

  const roastTypeLabelMap: Record<string, string> = {
    espresso: t('workOrders.beanSelection.roast.espresso'),
    omni: t('workOrders.beanSelection.roast.omni'),
    filter: t('workOrders.beanSelection.roast.filter'),
    sample: t('workOrders.beanSelection.roast.sample'),
  };

  // Typed against the canonical union so this list can never drift from the roast
  // types the work order form offers and the backend accepts.
  const roastTypeOptions: RoastType[] = ['espresso', 'omni', 'filter', 'sample'];

  const inventoryIndex = React.useMemo(() => {
    const map = new Map<string, { code?: string; name?: string }>();
    inventoryItems.forEach(item => {
      map.set(item.id, { code: item.code, name: item.name });
    });
    return map;
  }, [inventoryItems]);

  const filteredList = orders.filter(order => {
    const query = searchFilter.trim().toLowerCase();
    const inventoryInfo = order.itemId ? inventoryIndex.get(order.itemId) : undefined;
    const matchesSearch =
      query.length === 0 ||
      (order.workOrderNumber && order.workOrderNumber.toLowerCase().includes(query)) ||
      order.recipeName.toLowerCase().includes(query) ||
      (order.itemCode && order.itemCode.toLowerCase().includes(query)) ||
      (order.itemName && order.itemName.toLowerCase().includes(query)) ||
      (inventoryInfo?.code && inventoryInfo.code.toLowerCase().includes(query)) ||
      (inventoryInfo?.name && inventoryInfo.name.toLowerCase().includes(query));

    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(order.status);
    const matchesRoastType = roastTypeFilter.length === 0 || (order.roastType && roastTypeFilter.includes(order.roastType));
    const orderMachineId = order.machineTypeId ? machineTypeToMachineId.get(order.machineTypeId) : undefined;
    const matchesMachine = machineFilter.length === 0 || (orderMachineId ? machineFilter.includes(orderMachineId) : false);
    const matchesMachineType = machineTypeFilter.length === 0 || (order.machineTypeId ? machineTypeFilter.includes(order.machineTypeId) : false);
    return matchesSearch && matchesStatus && matchesRoastType && matchesMachine && matchesMachineType;
  });

  const machineOptions = React.useMemo(
    () => machines.map(m => ({ id: m.id, label: m.name })),
    [machines]
  );

  const machineTypeOptions = React.useMemo(() => {
    const source = machineFilter.length === 0
      ? machineTypes
      : machineTypes.filter(mt => machineFilter.includes(mt.machineId));
    return source.map(mt => ({
      id: mt.id,
      label: mt.name,
      machineName: mt.machineName,
    }));
  }, [machineTypes, machineFilter]);

  const derivedList: DerivedWorkOrder[] = filteredList.map(order => {
    const recipeName = order.recipeName;
    const input = order.inputWeight ?? null;
    const output = order.outputWeight ?? null;
    const shrinkageKg = input !== null && output !== null ? input - output : null;
    const shrinkagePercent = shrinkageKg !== null && input && input !== 0 ? (shrinkageKg / input) * 100 : null;
    const inventoryInfo = order.itemId ? inventoryIndex.get(order.itemId) : undefined;
    const resolvedItemCode = order.itemCode ?? inventoryInfo?.code ?? undefined;
    const resolvedItemName = order.itemName ?? inventoryInfo?.name ?? order.items[0]?.name ?? undefined;
    return {
      ...order,
      primaryRecipeName: recipeName,
      shrinkageKg,
      shrinkagePercent,
      resolvedItemCode,
      resolvedItemName,
    };
  });

  const { sortedItems: sortedOrders, sortConfig, handleSort } = useSort(derivedList);

  const { page, size, offset, setPage, setSize, resetOnChange } = usePaginationState({ defaultSize: 25 });
  resetOnChange([searchFilter, statusFilter, roastTypeFilter, machineFilter, machineTypeFilter, sortConfig?.key, sortConfig?.direction]);

  const totalOrders = sortedOrders.length;
  const filteredOrders = sortedOrders.slice(offset, offset + size);

  const shouldGroup = !sortConfig || sortConfig.key === 'targetDate';
  const groupedOrders = shouldGroup ? groupItemsByDate(filteredOrders, order => order.targetDate) : {};
  const sortedDates = shouldGroup ? Object.keys(groupedOrders).sort((a, b) => b.localeCompare(a)) : [];

  const formatDate = (value?: string) => {
    if (!value) return t('settingsMcp.values.none');
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return t('settingsMcp.values.none');
    return date.toLocaleDateString();
  };

  const formatNumber = (value: number | null | undefined, digits = 3) => {
    if (value === null || value === undefined) return null;
    if (!Number.isFinite(value)) return null;
    return value.toFixed(digits);
  };

  return {
    loading,
    orders,
    inventoryItems,
    editingOrder,
    setEditingOrder,
    isFinishedGoodsModalOpen,
    // Deliberately not exposing the raw setter: closing without clearing pendingCompletionId
    // would leave a work order queued to complete on the next unrelated modal submit.
    closeFinishedGoodsModal,
    finishedGoodInitialData,
    selectedWorkOrder,
    finishedGoodMode,
    handleEditOrder,
    handleUpdateOrder,
    handleStatusChange,
    handleCancelOrder,
    handleDeleteOrder,
    handleCompleteFromCard,
    handleMoveToInventory,
    handleCreateFinishedGood,
    statusOptions,
    roastTypeLabelMap,
    roastTypeOptions,
    searchFilter,
    setSearchFilter,
    statusFilter,
    setStatusFilter,
    roastTypeFilter,
    setRoastTypeFilter,
    machineFilter,
    setMachineFilter,
    machineTypeFilter,
    setMachineTypeFilter,
    machineOptions,
    machineTypeOptions,
    filteredOrders,
    totalOrders,
    page,
    pageSize: size,
    setPage,
    setPageSize: setSize,
    sortConfig,
    handleSort,
    shouldGroup,
    groupedOrders,
    sortedDates,
    formatDate,
    formatNumber,
    formatPercent,
    handleCreateWorkOrder,
    quotaError,
    clearQuotaError,
  };
};
